/**
 * RpgRatingsPage (ТЗ №5, «Рейтинг») — leaderboards from ``GET /api/rpg/ratings/``,
 * switchable by tab: rank / mora / primogems / wins / boss wins. Each row is a place,
 * player name and the (server-computed) value for that board.
 */
import { useState } from "react";

import { useBackButton } from "@melusine/shared-telegram";
import { useRpgRatings, type RpgRatingEntry, type RpgRatings } from "@entities/rpg";
import { StatePanel } from "@widgets/catalog-browser";
import { formatUnits } from "@shared/lib/format";
import { routes, useNavigate } from "@shared/router";

import { RpgHeader } from "./ui/RpgHeader";
import styles from "./rpg.module.css";

type Board = keyof RpgRatings;

const TABS: readonly { readonly key: Board; readonly label: string }[] = [
  { key: "by_rank", label: "Ранг" },
  { key: "by_mora", label: "Мора" },
  { key: "by_primogems", label: "Примогемы" },
  { key: "by_wins", label: "Победы" },
  { key: "by_boss_wins", label: "Боссы" },
];

export function RpgRatingsPage(): JSX.Element {
  const navigate = useNavigate();
  const query = useRpgRatings();
  const [board, setBoard] = useState<Board>("by_rank");

  useBackButton(() => {
    navigate(routes.rpg());
  });

  if (query.isLoading) {
    return <StatePanel kind="loading" />;
  }
  if (query.data === null) {
    return (
      <StatePanel
        kind="error"
        message="Не удалось загрузить рейтинги."
        onRetry={query.refetch}
      />
    );
  }

  const entries: readonly RpgRatingEntry[] = query.data[board];

  return (
    <div className={styles.page}>
      <RpgHeader title="Рейтинг" onBack={() => navigate(routes.rpg())} />

      <div className={styles.tabBar} role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={board === t.key}
            className={`${styles.tab} ${board === t.key ? styles.tabActive : ""}`}
            onClick={() => setBoard(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {entries.length === 0 ? (
        <StatePanel kind="empty" message="Пока нет данных." />
      ) : (
        <div className={styles.ratingList}>
          {entries.map((entry, index) => (
            <div key={entry.user_id} className={styles.ratingRow}>
              <span
                className={`${styles.ratingPlace} ${index === 0 ? styles.ratingTop1 : ""}`}
              >
                {index + 1}
              </span>
              <span className={styles.ratingName}>{entry.name}</span>
              <span className={styles.ratingValue}>{formatUnits(entry.value)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
